import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Loader2, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) { setErr("Enter your account email."); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Unable to send reset link. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Account</div>
        <h1 className="font-display font-black text-3xl uppercase tracking-tight text-tmf-navy mb-6">Forgot your password?</h1>

        {sent ? (
          <div data-testid="forgot-sent" className="border border-green-500 bg-green-50 p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="text-green-600 shrink-0 mt-0.5" size={22} />
              <div>
                <div className="font-display font-bold text-tmf-navy text-lg uppercase tracking-tight mb-1">Check your inbox</div>
                <p className="text-sm text-slate-700">
                  If an account exists for <b>{email}</b>, we've sent you a password-reset link. It expires in 30 minutes.
                </p>
                <p className="text-xs text-slate-500 mt-3 font-mono-tmf">Didn't get it? Check your spam folder or try again in a few minutes.</p>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <Button asChild variant="outline" className="rounded-none flex-1 uppercase tracking-widest text-[11px] font-bold">
                <Link to="/login">Back to Sign In</Link>
              </Button>
              <Button data-testid="forgot-resend" onClick={() => { setSent(false); setErr(""); }} variant="outline" className="rounded-none flex-1 uppercase tracking-widest text-[11px] font-bold">
                Resend
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">
              Enter the email address you used to sign up and we'll send you a link to reset your password.
            </p>
            <div>
              <label className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Email</label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="forgot-email"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-none pl-9"
                />
              </div>
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-2">{err}</div>}
            <Button data-testid="forgot-submit" type="submit" disabled={busy} className="rounded-none w-full h-12 bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest text-sm font-bold">
              {busy ? <><Loader2 size={16} className="animate-spin mr-2" /> Sending…</> : "Send Reset Link"}
            </Button>
            <div className="text-center text-sm text-slate-500 pt-2">
              Remembered it? <Link to="/login" className="text-tmf-blue font-bold hover:underline">Back to Sign In</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
